import {useCallback} from 'react';
import {t} from '../../../localization';
import {
  Absence,
  AbsenceType,
  useAbsencesOperations,
  useModalTask,
  useSnacksActions,
} from '../../../services';
import {useAlertTask} from '../alert';
import {AbsenceModal} from './AbsenceModal';

/**
 * Returns a function that can be used to open the Absence edit/add dialog
 * to add or edit an absence
 * @param teamId Team
 * @param absence Absence to edit - undefined to create a new absence
 * @param absenceType Absence type to edit - undefined to create a new absence
 */
export const useAddOrEditAbsenceTask = (
  teamId: number,
  absence?: Absence,
  absenceType?: AbsenceType,
) => {
  const retVal = useModalTask<boolean>(AbsenceModal);
  return useCallback(() => {
    retVal({teamId, absence, absenceType});
  }, [absence, absenceType, retVal, teamId]);
};

/**
 * Returns a function for deleting an absence element with a confirmation
 * dialog.
 * @param absence Absence to delete
 */
export const useDeleteAbsenceTask = (absence: Absence) => {
  const showAlert = useAlertTask();
  const {add: addSnack} = useSnacksActions();
  const {remove} = useAbsencesOperations();
  return useCallback(() => {
    showAlert({
      okButtonTitle: t('general.ok'),
      okButtonKind: 'Negative',
      title: t('screens.schedule.absence.delete.confirm.title'),
      body: t('screens.schedule.absence.delete.confirm.body'),
      cancelButtonTitle: t('general.cancel'),
    }).then(result => {
      if (result) {
        addSnack(t('screens.schedule.absence.delete.success'));
        remove(absence.id);
      }
    });
  }, [absence.id, addSnack, remove, showAlert]);
};
